import type { EntityNoticeVariant } from './params'

export type EntityNoticeEntryState = 'entry' | 'show-email'

export const ENTITY_NOTICE_TITLE_TEMPLATE = 'Vamos ativar os avisos de processos de {Nome}'
export const ENTITY_NOTICE_PICKER_TITLE_TEMPLATE =
  'Em qual e-mail você quer os avisos sobre {Nome}?'

export const ENTITY_NOTICE_WHATSAPP_LABEL = 'WhatsApp'
export const ENTITY_NOTICE_WHATSAPP_MASK_PLACEHOLDER = '(  ) _____-____'

export const ENTITY_NOTICE_DIVIDER_TEXT = 'ou'

export const ENTITY_NOTICE_EMAIL_LABEL = 'E-mail'
export const ENTITY_NOTICE_EMAIL_HELP_TEXT =
  'Você vai precisar entrar no e-mail e confirmar o endereço'

export const ENTITY_NOTICE_EMAIL_LINK_TEXT_A = 'Continuar com e-mail'
export const ENTITY_NOTICE_EMAIL_LINK_TEXT_C = 'Continuar com e-mail'

export const ENTITY_NOTICE_GOOGLE_BUTTON_TEXT = 'Continuar com Google'

// Diff line 10 — closes the bug the removed ENTITY_NOTICE_CONTINUE_BUTTON_TEXT
// ("Continuar") originated: this is the string actually measured on-screen
// for the e-mail-first shape's show-email CTA.
export const ENTITY_NOTICE_EMAIL_CTA_TEXT = 'Continuar com e-mail'
// Diff line 9 — variant B's own CTA, distinct from the e-mail-first shape's.
export const ENTITY_NOTICE_PICKER_CTA_TEXT = 'Ativar avisos'

// Diff line 4 — A and C's show-email chip strings diverge on purpose.
export const ENTITY_NOTICE_CHIP_IMMEDIATE_ACCESS = 'Acesso imediato'
export const ENTITY_NOTICE_CHIP_RECOMMENDED = 'Recomendado'

export const ENTITY_NOTICE_PICKER_GOOGLE_OPTION_LABEL = 'No meu email Google'
export const ENTITY_NOTICE_PICKER_OTHER_EMAIL_OPTION_LABEL = 'Em outro email'

export const ENTITY_NOTICE_LEGAL_TEXT_PREFIX =
  'Ao continuar você aceita criar uma conta gratuita no Jusbrasil e os '

export const ENTITY_NOTICE_LEGAL_LINK_LABELS = {
  terms: 'Termos de uso',
  privacy: 'Política de privacidade',
}

export function buildEntityNoticeTitle(entityName: string): string {
  return ENTITY_NOTICE_TITLE_TEMPLATE.replace('{Nome}', entityName)
}

export function buildEntityNoticePickerTitle(entityName: string): string {
  return ENTITY_NOTICE_PICKER_TITLE_TEMPLATE.replace('{Nome}', entityName)
}

export interface EntityNoticeEntryCopy {
  chipLabel: string | null
  emailLinkText: string | null
  ctaText: string | null
  googleButtonKind: 'primary' | 'secondary'
}

/**
 * Single resolution point for the (variant, state) copy pairs closed by the
 * Figma diff table — one string per cell, no scattered `if`s in components.
 * `variantB` never reaches the "google button" fields (it has its own
 * picker-card shape) but still resolves a chip/legal pair through here so
 * the caveat above stays in one place.
 */
export function resolveEntityNoticeCopy(
  variant: EntityNoticeVariant,
  state: EntityNoticeEntryState
): EntityNoticeEntryCopy {
  const isShowEmail = state === 'show-email'

  if (variant === 'variantB') {
    return {
      chipLabel: null,
      emailLinkText: null,
      ctaText: ENTITY_NOTICE_PICKER_CTA_TEXT,
      googleButtonKind: 'primary',
    }
  }

  if (variant === 'variantA') {
    return {
      chipLabel: isShowEmail ? ENTITY_NOTICE_CHIP_IMMEDIATE_ACCESS : null,
      emailLinkText: isShowEmail ? null : ENTITY_NOTICE_EMAIL_LINK_TEXT_A,
      ctaText: isShowEmail ? ENTITY_NOTICE_EMAIL_CTA_TEXT : null,
      googleButtonKind: isShowEmail ? 'secondary' : 'primary',
    }
  }

  // variantC
  return {
    chipLabel: isShowEmail ? ENTITY_NOTICE_CHIP_RECOMMENDED : null,
    emailLinkText: isShowEmail ? null : ENTITY_NOTICE_EMAIL_LINK_TEXT_C,
    ctaText: isShowEmail ? ENTITY_NOTICE_EMAIL_CTA_TEXT : null,
    googleButtonKind: isShowEmail ? 'secondary' : 'primary',
  }
}

export const ENTITY_NOTICE_FINALIZE_TITLE = 'Finalize seu cadastro'
export const ENTITY_NOTICE_FINALIZE_SUBTITLE =
  'Precisamos desses dados para identificar seus processos e exibir as movimentações para você.'

export const ENTITY_NOTICE_TERMINAL_TITLE = 'Interesse registrado!'
const ENTITY_NOTICE_TERMINAL_SUBTITLE_TEMPLATE =
  'Assim que o acompanhamento estiver disponível, seus avisos serão enviados para {Email}.'
export const ENTITY_NOTICE_TERMINAL_PRIMARY_BUTTON_TEXT = 'Ver meus processos'
export const ENTITY_NOTICE_TERMINAL_SECONDARY_BUTTON_TEXT = 'Fechar'

// Figma (Tela 10, Frame 1008805) completes this h2 with a phone number, since
// that reference design's channel is WhatsApp. Cells A and B do collect a
// phone (variant A's entry screen, both A and B's "Finalize seu cadastro"
// step) for WhatsApp opt-in, but the confirmation code and this notice still
// travel by e-mail (constants.ts §D-2) — so this cites the e-mail the visitor
// gave, never a phone (Invariante 4).
export function buildEntityNoticeTerminalSubtitle(email: string): string {
  return ENTITY_NOTICE_TERMINAL_SUBTITLE_TEMPLATE.replace('{Email}', email)
}
